import { useState } from "react";
import { useFeedbackStore } from "../../stores/feedback.js";
import { useRespondToInput } from "../../api/hooks.js";

export function InputRequestPanel() {
  const pendingInputs = useFeedbackStore((s) => s.pendingInputs);
  const removePendingInput = useFeedbackStore((s) => s.removePendingInput);
  const respond = useRespondToInput();

  if (pendingInputs.length === 0) return null;

  return (
    <div>
      <div className="px-5 pt-4 pb-2">
        <span className="text-xs font-medium text-amber-400 tabular-nums">
          Awaiting Input ({pendingInputs.length})
        </span>
      </div>
      {pendingInputs.map((input) => (
        <InputRequestItem
          key={input.feedbackMessageId ?? input.question}
          input={input}
          onRespond={(value) => {
            respond.mutate({
              runId: input.runId,
              body: {
                feedbackMessageId: input.feedbackMessageId,
                value,
              },
            });
            removePendingInput(input.feedbackMessageId);
          }}
        />
      ))}
    </div>
  );
}

function InputRequestItem({
  input,
  onRespond,
}: {
  input: any;
  onRespond: (value: unknown) => void;
}) {
  const [textValue, setTextValue] = useState("");

  const renderInput = () => {
    switch (input.inputType) {
      case "confirmation":
        return (
          <div className="flex gap-3">
            <button
              onClick={() => onRespond(true)}
              className="bg-brand-green/10 text-brand-green hover:bg-brand-green/20 px-4 py-1.5 rounded-lg text-sm font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
            >
              Approve
            </button>
            <button
              onClick={() => onRespond(false)}
              className="bg-brand-pink/10 text-brand-pink hover:bg-brand-pink/20 px-4 py-1.5 rounded-lg text-sm font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
            >
              Reject
            </button>
          </div>
        );

      case "choice":
        return (
          <div className="space-y-1.5">
            {(input.options ?? []).map((opt: any) => (
              <button
                key={opt.value}
                onClick={() => onRespond(opt.value)}
                className="w-full text-left hover:bg-surface-lighter/20 rounded-lg px-3 py-2 text-sm text-text-secondary hover:text-text-primary transition-[color,background-color,transform] duration-200 active:scale-[0.98]"
              >
                {opt.label}
              </button>
            ))}
          </div>
        );

      case "text":
      default:
        return (
          <div className="flex gap-3">
            <input
              type="text"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              placeholder="Type your response..."
              className="flex-1 bg-transparent border-b border-border-subtle/40 px-0 py-2 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-purple transition-colors duration-200"
              onKeyDown={(e) => {
                if (e.key === "Enter" && textValue.trim()) {
                  onRespond(textValue.trim());
                }
              }}
            />
            <button
              onClick={() => {
                if (textValue.trim()) onRespond(textValue.trim());
              }}
              className="bg-brand-purple hover:bg-brand-purple/80 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
            >
              Send
            </button>
          </div>
        );
    }
  };

  return (
    <div className="px-5 py-4">
      <p className="text-sm text-text-primary mb-3 leading-relaxed">{input.question}</p>
      {renderInput()}
    </div>
  );
}
